import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { clearPwaReloadFlag, registerPwaServiceWorker, subscribePwaServiceWorkerEvents } from '@/lib/pwaUpdates'

const START_DRAG = 18
const THRESHOLD = 118
const MAX_PULL = 150

const PwaLifecycle = () => {
    const startY = useRef(0)
    const pulling = useRef(false)
    const refreshing = useRef(false)
    const pullDistanceRef = useRef(0)
    const frameRef = useRef<number | null>(null)
    const [pullDistance, setPullDistance] = useState(0)

    const setPull = (value: number) => {
        pullDistanceRef.current = value
        if (frameRef.current !== null) return
        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = null
            setPullDistance(pullDistanceRef.current)
        })
    }

    useEffect(() => {
        clearPwaReloadFlag()
        registerPwaServiceWorker()
        return subscribePwaServiceWorkerEvents()
    }, [])

    useEffect(() => {
        const canStartPull = (target: EventTarget | null) => {
            if (window.scrollY > 0) return false
            if (!(target instanceof Element)) return true
            return !target.closest('[data-pull-refresh-ignore], input, textarea, select, [role="dialog"]')
        }

        const onTouchStart = (event: TouchEvent) => {
            if (event.touches.length !== 1 || refreshing.current || !canStartPull(event.target)) return
            startY.current = event.touches[0].clientY
            pulling.current = true
        }

        const onTouchMove = (event: TouchEvent) => {
            if (!pulling.current || refreshing.current || event.touches.length !== 1) return
            const delta = event.touches[0].clientY - startY.current
            if (delta <= START_DRAG) {
                setPull(0)
                return
            }

            event.preventDefault()
            const eased = Math.min(MAX_PULL, (delta - START_DRAG) * 0.34)
            setPull(eased)
        }

        const onTouchEnd = () => {
            if (!pulling.current || refreshing.current) return
            pulling.current = false

            if (pullDistanceRef.current >= THRESHOLD) {
                refreshing.current = true
                setPull(THRESHOLD)
                window.location.reload()
                return
            }

            setPull(0)
        }

        window.addEventListener('touchstart', onTouchStart, { passive: true })
        window.addEventListener('touchmove', onTouchMove, { passive: false })
        window.addEventListener('touchend', onTouchEnd, { passive: true })
        window.addEventListener('touchcancel', onTouchEnd, { passive: true })

        return () => {
            if (frameRef.current !== null) {
                window.cancelAnimationFrame(frameRef.current)
            }
            window.removeEventListener('touchstart', onTouchStart)
            window.removeEventListener('touchmove', onTouchMove)
            window.removeEventListener('touchend', onTouchEnd)
            window.removeEventListener('touchcancel', onTouchEnd)
        }
    }, [])

    const visible = pullDistance > 0
    const ready = pullDistance >= THRESHOLD
    const progress = Math.min(1, pullDistance / THRESHOLD)

    return (
        <div className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[70] flex justify-center px-4">
            <div
                className="flex h-11 min-w-[170px] origin-top items-center justify-center gap-2 rounded-full border border-border/60 bg-background/95 px-4 text-center text-xs font-medium text-foreground shadow-lg backdrop-blur will-change-transform"
                style={{
                    opacity: visible ? 1 : 0,
                    transform: `translate3d(0, ${visible ? Math.min(28, pullDistance * 0.16) : -18}px, 0) scale(${0.9 + progress * 0.1})`,
                    transition: visible ? 'opacity 120ms ease-out' : 'opacity 160ms ease-out, transform 180ms ease-out',
                }}
            >
                <Loader2 className={`h-4 w-4 ${ready ? 'animate-spin' : ''}`} />
                <span>{ready ? 'Release to refresh' : 'Pull to refresh'}</span>
            </div>
        </div>
    )
}

export default PwaLifecycle
